/** Knowledge Base list (/w/:ws/agents/:agentId/knowledge). */
export const knowledge = {
  title: "Knowledge Base",
  subtitle: "Documents, web pages and Q&As your agents answer from.",
  subtitleAgent: "{{agent}} answers from {{count}} of them.",
  resyncAll: "Re-sync all",
  resyncStarted_one: "Re-sync started for {{count}} source — websites re-crawled, files re-checked for changes",
  resyncStarted_other: "Re-sync started for {{count}} sources — websites re-crawled, files re-checked for changes",
  addSource: "Add source",
  stats: {
    sources: "Sources",
    sourcesBreakdown: "{{files}} files · {{sites}} sites · {{other}} Q&A/text",
    chunks: "Chunks",
    chunksExcluded: "{{count}} excluded from answers",
    chunksNoneExcluded: "none excluded",
    uploaded: "Uploaded",
    uploadedNote: "original files and pages",
    storage: "Storage",
    storageOf: "{{used}} MB of {{limit}} MB plan",
    storageUsed: "{{used}} MB used",
    storageUnknown: "Plan storage unavailable",
    storageLabel: "Storage used",
    lastSync: "Last sync",
    attention_one: "{{count}} source needs attention",
    attention_other: "{{count}} sources need attention",
    allHealthy: "All sources healthy",
    syncing_one: "{{count}} source syncing",
    syncing_other: "{{count}} sources syncing",
  },
  banner: {
    title_one: "{{count}} source failed to process",
    title_other: "{{count}} sources failed to process",
    body: "{{name}}: {{reason}} Answers can't use it until it's fixed.",
    bodyMore: "and {{count}} more.",
    retry: "Re-index {{name}}",
    dismiss: "Dismiss",
    noReason: "Processing failed.",
  },
  tabs: {
    label: "Source types",
    all: "All",
    files: "Files",
    websites: "Websites",
    qa: "Q&A",
    text: "Text",
  },
  filter: {
    search: "Filter by name, status or access…",
    type: "Type",
    allTypes: "All types",
    access: "Access level",
    allAccess: "All access levels",
  },
  types: {
    pdf: "PDF",
    spreadsheet: "Spreadsheet",
    word: "Word",
    slides: "PowerPoint",
    image: "Image",
    website: "Website",
    qa: "Q&A",
    text: "Text snippet",
  },
  table: {
    caption: "Knowledge sources",
    name: "Name",
    type: "Type",
    access: "Access",
    status: "Status",
    chunks: "Chunks",
    updated: "Updated",
    actions: "Actions",
    pages_one: "{{count}} page",
    pages_other: "{{count}} pages",
    version: "v{{version}}",
    showing: "Showing {{shown}} of {{total}} sources",
    previous: "Previous",
    next: "Next",
    noAccess: "No audience",
    more: "+{{count}}",
  },
  stages: {
    uploading: "Uploading",
    queued: "Queued",
    parsing: "Parsing (OCR)",
    chunking: "Chunking",
    contextualizing: "Contextualizing",
    embedding: "Embedding",
    ready: "Ready",
    failed: "Failed",
    disabled: "Disabled",
    progress: "{{name}}: {{stage}} {{percent}}%",
    processingPages: "Processing · {{done}}/{{total}} pages",
    progressPages: "{{name}}: processing, {{done}} of {{total}} pages, {{percent}}%",
    readyWithWarnings_one: "Ready · {{count}} page failed",
    readyWithWarnings_other: "Ready · {{count}} pages failed",
    details: "Details",
    detailsFor: "View details for {{name}}",
  },
  live: {
    connected: "Live status",
    polling: "Live status reconnecting — refreshing every few seconds",
  },
  menu: {
    label: "Source actions: {{name}}",
    view: "View chunks",
    reindex: "Re-index",
    access: "Change access",
    delete: "Delete",
    reindexStarted: "Re-indexing started — answers keep using the current version until it finishes",
    recrawlStarted: "Re-crawl queued for {{name}}",
  },
  empty: {
    title: "No knowledge yet",
    body: "Add documents, a website, or Q&As. Your agents answer only from what you add here.",
    filteredTitle: "No sources match these filters",
    filteredBody: "Try a different type or access level.",
    clearFilters: "Clear filters",
    loadError: "Sources couldn't load",
  },
  callout: {
    title: "Drop files here, or click to browse",
    body: "PDF, Word, Excel, PowerPoint, images and scanned documents · up to {{mb}} MB each · scans are OCR'd automatically (English & Filipino)",
  },
  pipeline: {
    title: "How your documents are used",
    subtitle: "Every source goes through the same pipeline. Nothing is used to train public AI models.",
    parsed: "Parsed",
    parsedBody: "Text, headings and tables are read — scans go through OCR.",
    chunked: "Split into chunks",
    chunkedBody: "Short passages (~500 tokens) that follow the document's sections.",
    embedded: "Embedded",
    embeddedBody: "Each chunk becomes a vector that captures its meaning.",
    searched: "Searched",
    searchedBody: "By meaning and by keywords, so a question in Filipino still finds the English passage.",
    reranked: "Reranked",
    rerankedBody: "A second model picks the 3–5 passages that best answer the question.",
    answered: "Answer with citations",
    answeredBody: "The reply links back to the exact page so people can verify it.",
    residency: "Access levels are enforced at step 04: a visitor's search never sees chunks outside their audience. Files are stored encrypted in the Singapore region in line with the Data Privacy Act of 2012.",
  },
  add: {
    title: "Add knowledge source",
    description: "Sources belong to the workspace. Link them to agents in Agent Settings.",
    tabFiles: "Files",
    tabWebsite: "Website",
    tabQa: "Q&A",
    tabText: "Text snippet",
    tabDrive: "Google Drive",
    storageLeft: "{{mb}} MB of storage left",
    submit: "Add source",
    done: "Done",
    cancel: "Cancel",
    audiences: "Who can see this",
    audiencesHint: "Answers built from this source only reach these audiences.",
    audiencesRequired: "Pick at least one audience",
    accessWebsiteHint: "A website starts as {{label}}, because anyone can already read it.",
    accessPrivateHint: "Starts as {{label}}, so nothing you add is visible to guests until you choose {{everyone}}.",
    guestCaution: "If this content contains personal data, guests will be able to read it. Remove it first or pick a narrower audience.",
    agents: "Use in agents",
    agentsHint: "These agents will answer from the new source. You can change this any time in Agent Settings > Knowledge.",
    agentsNone: "There are no agents you can add this source to yet.",
    dropTitle: "Drop files here, or click to browse",
    dropHint: "PDF, DOCX, XLSX, PPTX, CSV, TXT, JPG/PNG · max {{mb}} MB each",
    dropActive: "Release to upload",
    rejectType: "{{name}}: this file type isn't supported",
    rejectSize: "{{name}}: larger than {{mb}} MB",
    rejectEmpty: "{{name}}: the file is empty",
    rejectCount: "{{name}}: too many files at once (max {{max}})",
    filesRequired: "Choose at least one file first",
    selectedFiles: "Files to upload",
    remove: "Remove {{name}}",
    uploadsTitle: "Uploads",
    unchanged: "Already up to date — nothing changed",
    retry: "Retry",
    retryFile: "Retry {{name}}",
    storageFailed: "The file couldn't reach storage. Check your connection and try again.",
    infected: "This file was blocked by the virus scan.",
    filesAdded_one: "{{count}} file added to the Knowledge Base",
    filesAdded_other: "{{count}} files added to the Knowledge Base",
    ocrNote: "Scanned pages and images are detected and OCR'd automatically.",
    url: "Website URL",
    urlHint: "We follow links on the same domain and respect robots.txt.",
    urlInvalid: "Enter a full https:// address",
    scope: "Crawl scope",
    scopeOne: "1 — this page only",
    scopeLinked: "Page + linked pages (up to 25)",
    scopeSection: "Whole section (up to 100)",
    scopeSite: "Whole site (max 500 pages)",
    include: "Include patterns",
    exclude: "Exclude patterns",
    patternsHint: "Comma-separated. Old news posts often contain outdated information — exclude them.",
    preview: "Preview crawl",
    previewing: "Checking site…",
    previewTitle: "{{count}} pages found",
    previewCapped: "{{count}}+ pages found (limit reached)",
    previewFiles_one: "{{count}} linked file",
    previewFiles_other: "{{count}} linked files",
    robotsAllowed: "robots.txt allows crawling",
    robotsBlocked: "robots.txt blocks this start page",
    sitemapFound: "Sitemap found",
    crawlQueued: "Crawl queued — {{count}} pages found",
    crawlQueuedPlain: "Crawl queued",
    question: "Question",
    questionRequired: "Write the question",
    answer: "Answer",
    answerRequired: "Write the answer",
    qaHint: "Q&As are matched first and always cited by name.",
    qaAdded: "Q&A added — live in answers within a minute",
    textTitle: "Title",
    textTitleRequired: "Give the snippet a title",
    textBody: "Content",
    textBodyRequired: "Paste or write the content",
    textAdded: "Text snippet saved and indexed",
    driveTitle: "Google Drive isn't available yet",
    driveBody: "Folder sync from Google Drive is coming. For now, download the files and upload them in the Files tab.",
    driveCta: "Go to Files",
  },
  pii: {
    chip: "May contain {{kinds}}",
    title: "Personal data was found when this source was processed.",
  },
  access: {
    title: "Change access level",
    piiWarning: "“{{name}}” appears to contain {{kinds}}. Everyone includes guests who haven't signed in.",
    description: "Pick every audience that may see answers from {{name}}.",
    save: "Save access",
    saved: "Access updated — re-filtering {{count}} chunks",
  },
  delete: {
    title: "Delete this source?",
    body: "All of its {{count}} chunks are removed from every agent. Past conversations keep their citations as plain text. This can't be undone.",
    confirm: "Delete source",
    deleted: "Source deleted — its chunks were removed from all agents",
  },
} as const;
